import { ReactElement, createContext, useContext, useState } from 'react';

import styled from 'styled-components';

import { Checkbox } from '@components/checkbox/checkbox';
import { FormError } from '@components/form-error';
import { FormLabel } from '@components/form-label';
import { IconProps } from '@components/icons';
import { Stack } from '@components/stack';
import { useEvent } from '@hooks/use-event';
import { noop } from '@utils/browser';
import { pxToRem } from '@utils/converters';

type CheckboxGroupContextType = {
  selected: string[];
  toggle(value: string): void;
};

const CheckboxGroupContext = createContext<CheckboxGroupContextType>({
  selected: [],
  toggle: noop,
});

export type CheckboxGroupProps = {
  label?: string;
  onChange?(values: string[]): void;
  children: ReactElement[];
  defaultValue?: string[];
  value?: string[];
  layout?: 'base' | 'snug';
  error?: string;
  labelIcon?: ReactElement<IconProps>;
};

export const CheckboxGroup = ({
  label,
  defaultValue = [],
  value: userValue,
  children,
  onChange,
  layout = 'base',
  error,
  labelIcon,
}: CheckboxGroupProps) => {
  const [selected, setSelected] = useState<string[]>(defaultValue);
  const values = userValue ?? selected;

  const toggle = useEvent((value: string) => {
    const idx = values.findIndex((val) => val === value);
    const updated = values.slice();

    if (idx !== -1) {
      updated.splice(idx, 1);
    } else {
      updated.push(value);
    }

    setSelected(updated);
    onChange?.(updated);
  });

  return (
    <CheckboxGroupContext.Provider value={{ selected: values, toggle }}>
      <Stack space={1}>
        {label && <FormLabel text={label} icon={labelIcon} />}
        <Container data-layout={layout}>{children}</Container>
        <FormError text={error} />
      </Stack>
    </CheckboxGroupContext.Provider>
  );
};

type CheckboxItemProps = {
  value: string;
  label: string | JSX.Element;
};

const CheckboxItem = ({ label, value }: CheckboxItemProps) => {
  const { selected, toggle } = useContext(CheckboxGroupContext);
  const isSelected = selected.includes(value);

  return <Checkbox value={isSelected} label={label} onChange={() => toggle(value)} />;
};

CheckboxGroup.Item = CheckboxItem;

const Container = styled.div({
  '> div': {
    display: 'flex',
    alignItems: 'center',
  },

  '&[data-layout="base"]': {
    '> div': {
      minHeight: pxToRem(40),
    },
  },

  '&[data-layout="snug"]': {
    '> div': {
      minHeight: pxToRem(32),
    },
  },
});
